import './orders.scss';
import { Enum, PaginationList } from '../../../../core/domain';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { Order, OrderCount, OrderFilterValues, SortDirection } from '../../../../core/domain/order/order';
import { getIconPath } from '../../../../core/utils/getIconPath';
import { Divider, Dropdown, Menu, Tag } from 'antd';
import { digitSperator, faNumToEnNum } from '../../../../core/utils/decimalExtentions';
import moment from 'jalali-moment';
import { Badge } from '../../../../components';
import { ExportOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { ReactComponent as CalendarIcon } from 'assets/icons/dash-line-icons/calendar.svg';
import {
    getAllOrders,
    getMarketTypes,
    getOrderSides,
    getOrderSources,
    getOrderState,
    getOrderType,
} from '../../../../core/repositories/orders';
import { getPageNum } from '../../../../core/utils/paggingHelper';
import { Card } from '../../../../components/card/card';
import { Input, inputType } from '../../../../components/input/input';
import { Button, buttonType } from '../../../../components/button/button';
import { Table } from '../../../../components/table/table';
import { Dropdown as CustomDropdown } from 'components/dropdown/dropdown';
import DatePicker from 'react-multi-date-picker';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import * as React from 'react';
import { OrderDetailModal } from './order-detail/order-detail';
import { Page } from 'components/page/page';
import { BadgeType } from 'components/badge/badge';
import { downloadCSV } from 'core/utils/export-to-file';
import { jFormat } from 'core/utils/jFormat';

const initialFilters = {
    marketType: {
        id: -1,
        title: 'نوع ارز',
    },
    state: {
        id: -1,
        title: 'همه وضعیت ها',
    },
    sort: SortDirection.ASC,
    type: {
        id: -1,
        title: 'همه سفارش ها',
    },
    source: {
        id: -1,
        title: 'همه مبدا ها',
    },
    side: {
        id: -1,
        title: 'سمت معامله',
    },
};

export const Orders = () => {
    const [orderList, setOrderList] = useState<PaginationList<Order>>();
    const [ordersCount, setOrdersCount] = useState<OrderCount>();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [cardLoading, setCardLoading] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingPaging, setLoadingPaging] = useState<boolean>(false);

    //Enums
    const [marketTypes, setMarketTypes] = useState<Enum[]>([initialFilters.marketType]);
    const [orderStates, setOrderStates] = useState<Enum[]>([initialFilters.state]);
    const [orderSides, setOrderSides] = useState<Enum[]>([initialFilters.side]);
    const [orderTypes, setOrderTypes] = useState<Enum[]>([initialFilters.type]);
    const [orderSources, setOrderSources] = useState<Enum[]>([initialFilters.source]);

    const [pageSize, setPageSize] = useState<number>(10);

    const [query, setQuery] = useState<string>('');

    const [filterValues, setFilterValues] = useState<OrderFilterValues>(initialFilters);

    const [selectedItem, setSelectedItem] = useState<Order>();

    const startDateRef = useRef();
    const endDateRef = useRef();

    const getOrderStatusBadgeType = (stateId: number): BadgeType => {
        switch (stateId) {
            case 1: // باز
                return 'waiting';
            case 2: // منقضی شده
                return 'secondary';
            case 3: // بسته شده
                return 'success';
            case 4: // کنسل شده
            case 5: // رد شده
                return 'error';
            default:
                return 'secondary';
        }
    };

    const columns: any[] = [
        {
            name: '#',
            id: 'index',
            selector: (row: Order, rowIndex: number) => rowIndex + 1,
            width: '3%',
        },
        {
            name: 'توسط',
            id: 'createdBy',
            width: '10%',
            cell: (row: Order) => (
                <div className="column-createdBy">
                    <span className="name">{row.createdBy}</span>
                    <p className="phone">{row.phoneNumber}</p>
                </div>
            ),
        },
        {
            name: 'شناسه ',
            id: 'trackingCode',
            width: '9%',
            selector: (row: Order) => row.trackingCode,
            cell: (row: Order) => <p className="en">{row.trackingCode}</p>,
        },
        {
            name: 'جفت',
            id: 'pair',
            selector: (row: Order) => row.pair.base + row.pair.quote,
            cell: (row: Order) => (
                <div className="cell pair">
                    <img src={getIconPath(row.pair.base.toLowerCase())} />
                    <span className="en">{row.pair.base}</span>
                    {` `} / {` `}
                    <span className="en">{row.pair.quote}</span>
                    <img src={getIconPath(row.pair.quote.toLowerCase())} />
                </div>
            ),
            width: '8%',
        },
        {
            name: 'خرید/فروش',
            id: 'side',
            width: '7%',
            selector: (row: Order) => row.side.title,
            cell: (row: Order) => (
                <>
                    <Tag color={`${row.side.id === 1 ? 'green' : 'red'}`}>{row.side.title}</Tag>
                </>
            ),
        },
        {
            name: 'مقدار پرداخت شده',
            id: 'sourceQuantity',
            width: '7%',
            selector: (row: Order) => row.sourceQuantity,
            cell: (row: Order) => (
                <div className="value-item">
                    <span className="en">{row.side.id === 2 ? row.pair.base : row.pair.quote}</span>
                    <span>
                        {row.side.id === 1
                            ? digitSperator(row.sourceQuantity.toFixed(0))
                            : digitSperator(row.sourceQuantity)}
                    </span>
                </div>
            ),
        },
        {
            name: 'مقدار دریافت شده',
            id: 'destinationQuantity',
            width: '7%',
            selector: (row: Order) => row.destinationQuantity,
            cell: (row: Order) => (
                <div className="value-item">
                    <span className="en">{row.side.id === 2 ? row.pair.quote : row.pair.base}</span>
                    <span>
                        {row.side.id === 2
                            ? digitSperator(row.destinationQuantity.toFixed(0))
                            : digitSperator(row.destinationQuantity)}
                    </span>
                </div>
            ),
        },
        {
            name: 'قیمت واحد (تومان)',
            id: 'onPrice',
            width: '8%',
            selector: (row: Order) => digitSperator(row.onPrice),
        },
        {
            name: 'مبدا سفارش',
            id: 'source',
            width: '7%',
            selector: (row: Order) => row.side.title,
            cell: (row: Order) => (
                <>
                    <Tag color={`${row.source.id === 1 ? 'cyan' : 'purple'}`}>{row.source.title}</Tag>
                </>
            ),
        },
        {
            name: 'تاریخ ایجاد',
            id: 'createdAt',
            width: '6%',
            selector: (row: Order) => row.createdOn,
            cell: (row: Order) => (
                <div className="column-date">
                    <span className="day">
                        {moment.utc(row.createdOn).local().toDate().toLocaleDateString('fa-IR')}
                    </span>
                    <br />
                    <span className="time">{moment.utc(row.createdOn).local().format('HH:mm')}</span>
                </div>
            ),
        },
        {
            name: 'وضعیت',
            id: 'state',
            width: '5%',
            selector: (row: Order) => row.state.id,
            cell: (row: Order) => (
                <Badge
                    text={row.state.id == 3 ? 'موفق' : row.state.title}
                    type={getOrderStatusBadgeType(row.state.id)}
                />
            ),
        },
        {
            name: 'کارمزد',
            id: 'commission',
            width: '8%',
            selector: (row: Order) => row.commission,
            cell: (row: Order) => <span>{digitSperator(row.commission)} تومان </span>,
        },
        {
            name: 'مقدار دلاری',
            id: 'inDollarAmount',
            width: '8%',
            selector: (row: Order) => row.inDollarAmount,
            cell: (row: Order) => <span>{digitSperator(row.inDollarAmount)} واحد </span>,
        },
        {
            name: 'جزئیات',
            id: 'detail',
            width: '7%',
            cell: (row: Order) => <InfoCircleOutlined onClick={() => handleModal(row)} />,
        },
    ];

    function handleModal(id: Order) {
        setIsOpen(true);
        setSelectedItem(id);
    }

    const handleEnums = async () => {
        await getMarketTypes(setLoading, setMarketTypes);
        await getOrderState(setLoading, setOrderStates);
        await getOrderSides(setLoading, setOrderSides);
        await getOrderSources(setLoading, setOrderSources);
        // await getOrderType(setLoading, setOrderTypes);
    };

    const filterHandle = () => {
        var q = '';

        if (filterValues.side.id !== -1) q += `${q.includes('?') ? '&' : '?'}side=${filterValues.side.id}`;
        if (filterValues.startDate !== '')
            q += `${q.includes('?') ? '&' : '?'}startDate=${jFormat(filterValues.startDate)}`;
        if (filterValues.endDate !== '')
            q += `${q.includes('?') ? '&' : '?'}endDate=${jFormat(filterValues.endDate)}`;
        if (filterValues.keywords) q += `${q.includes('?') ? '&' : '?'}keywords=${filterValues.keywords}`;
        if (filterValues.marketType?.id !== -1)
            q += `${q.includes('?') ? '&' : '?'}marketType=${filterValues.marketType?.id}`;
        if (filterValues.source?.id !== -1) q += `${q.includes('?') ? '&' : '?'}source=${filterValues.source?.id}`;
        if (filterValues.type?.id !== -1 && filterValues.type !== undefined)
            q += `${q.includes('?') ? '&' : '?'}type=${filterValues.type?.id}`;
        setQuery(q);
        if (filterValues.state?.id !== -1 && filterValues.state !== undefined)
            q += `${q.includes('?') ? '&' : '?'}state=${filterValues.state?.id}`;
        setQuery(q);
        getAllOrders(setLoading, setOrderList, 1, pageSize, q, setOrdersCount);
    };

    const onFilterHandler = () => {
        getAllOrders(setLoading, setOrderList, 1, pageSize, query, setOrdersCount);
    };

    const updatePageSize = (newPageSize: number) => {
        setOrdersCount((prevState: OrderCount | undefined) => ({
            ...prevState!,
            pageSize: newPageSize,
        }));
    };

    useEffect(() => {
        handleEnums();
        !orderList && getAllOrders(setCardLoading, setOrderList, getPageNum(), pageSize, query, setOrdersCount);
    }, []);

    return (
        <Page className="pmx-orders" title="مدیریت سفارشات">
            <Card title="سفارشات" blur={cardLoading}>
                <div className="filter-row">
                    <div className="fields">
                        {selectedItem && (
                            <OrderDetailModal isOpen={isOpen} setIsOpen={setIsOpen} id={selectedItem?.id} />
                        )}
                        <Input
                            type={inputType.text}
                            value={filterValues.keywords}
                            placeholder="جستجو کنید ..."
                            className="search"
                            name="search"
                            onPressEnter={filterHandle}
                            onChange={(e) =>
                                setFilterValues({
                                    ...filterValues,
                                    keywords: e.target.value,
                                })
                            }
                        />

                        <CustomDropdown
                            items={marketTypes}
                            onChangeHandle={(value) =>
                                setFilterValues({
                                    ...filterValues,
                                    marketType: marketTypes?.find((l) => l.id === value),
                                })
                            }
                            selectedOption={filterValues.marketType}
                            defaultSelected={filterValues.marketType}
                        />

                        <CustomDropdown
                            items={orderSides}
                            onChangeHandle={(value) =>
                                setFilterValues({
                                    ...filterValues,
                                    side: orderSides?.find((l) => l.id === value),
                                })
                            }
                            selectedOption={filterValues.side}
                            defaultSelected={filterValues.side}
                        />

                        <CustomDropdown
                            items={orderStates}
                            onChangeHandle={(value) =>
                                setFilterValues({
                                    ...filterValues,
                                    state: orderStates?.find((l) => l.id === value),
                                })
                            }
                            selectedOption={filterValues.state}
                            defaultSelected={filterValues.state}
                        />

                        {/*<CustomDropdown*/}
                        {/*    items={orderTypes}*/}
                        {/*    onChangeHandle={(value) => setFilterValues({*/}
                        {/*        ...filterValues,*/}
                        {/*        type: orderTypes?.find((l) => l.id === value)*/}
                        {/*    })}*/}
                        {/*    selectedOption={filterValues.type}*/}
                        {/*    defaultSelected={filterValues.type}*/}
                        {/*/>*/}

                        <CustomDropdown
                            items={orderSources}
                            onChangeHandle={(value) =>
                                setFilterValues({
                                    ...filterValues,
                                    source: orderSources?.find((l) => l.id === value),
                                })
                            }
                            selectedOption={filterValues.source}
                            defaultSelected={filterValues.source}
                        />

                        <DatePicker
                            className="date-picker"
                            value={filterValues.startDate}
                            calendar={persian}
                            ref={startDateRef}
                            locale={persian_fa}
                            render={
                                <div>
                                    <Input
                                        value={filterValues.startDate}
                                        icon={<CalendarIcon />}
                                        placeholder="از تاریخ"
                                    />
                                </div>
                            }
                            onChange={(e) => {
                                setFilterValues({
                                    ...filterValues,
                                    startDate: e.format(),
                                });
                            }}
                        />
                        <DatePicker
                            className="date-picker"
                            value={filterValues.endDate}
                            calendar={persian}
                            ref={endDateRef}
                            locale={persian_fa}
                            render={
                                <div>
                                    <Input
                                        value={filterValues.endDate}
                                        icon={<CalendarIcon />}
                                        placeholder="تا تاریخ"
                                    />
                                </div>
                            }
                            onChange={(e) => {
                                setFilterValues({
                                    ...filterValues,
                                    endDate: e.format(),
                                });
                            }}
                        />
                    </div>

                    <Divider type="vertical" />
                    <div className="buttons">
                        <Button
                            text="خروجی اکسل"
                            type={buttonType.info}
                            icon={<ExportOutlined />}
                            onClick={(event) => downloadCSV('سفارشات', orderList.items)}
                        />
                        <Button
                            loading={loading}
                            disabled={loading}
                            onClick={() => filterHandle()}
                            text="اعمال فیلتر"
                            type={buttonType.success}
                        />
                        <Button
                            type={buttonType.error}
                            text={'حذف فیلتر'}
                            onClick={() => {
                                setFilterValues({
                                    keywords: '',
                                    startDate: '',
                                    endDate: '',
                                    side: initialFilters.side,
                                    source: initialFilters.source,
                                    marketType: initialFilters.marketType,
                                });
                                getAllOrders(setCardLoading, setOrderList, getPageNum(), pageSize, '', setOrdersCount);
                            }}
                        />
                    </div>
                </div>
                {orderList && (
                    <Table
                        filters={query}
                        setloader={setCardLoading}
                        setdata={setOrderList}
                        getdata={getAllOrders}
                        pagination={ordersCount}
                        data={orderList.items}
                        column={columns}
                        setPageSize={updatePageSize as Dispatch<SetStateAction<number>>}
                        pageSize={ordersCount?.pageSize}
                        setCount={setOrdersCount}
                        className={'orders-table'}
                    />
                )}
            </Card>
        </Page>
    );
};
